import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Building2, Lock, Mail, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalRegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('invite') || '';

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!inviteCode) {
      setError('No invite code provided');
      setLoading(false);
      return;
    }
    axios.get(`${API}/api/portal/invite/${inviteCode}`)
      .then(({ data }) => {
        setInvite(data);
        setEmail(data.email || '');
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invalid invite');
      })
      .finally(() => setLoading(false));
  }, [inviteCode]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/portal/register`, {
        invite_code: inviteCode,
        name,
        email,
        password,
      });
      localStorage.setItem('portal_token', data.token);
      localStorage.setItem('portal_user', JSON.stringify(data.user));
      toast.success('Account created successfully!');
      navigate('/portal');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-400">Validating invite...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-8 max-w-md w-full text-center">
          <Lock className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <h2 className="text-white text-lg font-semibold mb-2">Invalid Invite</h2>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <Building2 className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-white text-xl font-bold">ETA Dealer Portal</h1>
          <p className="text-slate-400 text-sm mt-1">Create your account for {invite?.dealership_name}</p>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="John Smith"
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                required
                data-testid="portal-register-name"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@dealership.com"
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                required
                data-testid="portal-register-email"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                required
                data-testid="portal-register-password"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm password"
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                required
                data-testid="portal-register-confirm"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold"
            data-testid="portal-register-submit"
          >
            {submitting ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-xs text-slate-500 mt-4">
          Already have an account? <a href="/portal/login" className="text-red-400 hover:text-red-300">Sign in</a>
        </p>
      </div>
    </div>
  );
}
